/**
 * Доступ к доске задач разработки для оболочки: есть ли доска у человека (ссылка в «Ходе разработки» и пункты
 * быстрого перехода). Без доступа сервер отвечает 403 — ссылок просто нет.
 */
import type { BoardMe } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';

/** Адреса доски в API: под /platform, как кабинет портала — это не действия в компании */
export const BOARD_API = '/platform/devboard';

export const boardMeQuery = {
  queryKey: ['devboard', 'me'] as const,
  queryFn: () => api<BoardMe>(`${BOARD_API}/me`),
  staleTime: 5 * 60_000,
  retry: false,
};

/** Доска человека или null; в демо-компании и в режиме «глазами пользователя» не спрашиваем */
export function useDevBoardMe(enabled = true): BoardMe | null {
  const me = useMe();
  const q = useQuery({ ...boardMeQuery, enabled: enabled && !me.viewAs && !me.tenant?.isDemo });
  return q.data ?? null;
}

/** Пункты быстрого перехода (Ctrl+K) — только тем, у кого есть доска */
export function useDevBoardPaletteItems(open: boolean) {
  const board = useDevBoardMe(open);
  return useMemo(
    () =>
      board
        ? [
            {
              key: 'devboard',
              title: 'Доска задач разработки',
              hint: 'Задачи чатов и команды портала: кто что взял',
              to: '/devboard',
            },
            {
              key: 'devboard-free',
              title: 'Доска задач разработки: свободные задачи',
              hint: 'Взять задачу в работу',
              to: '/devboard?taken=free',
            },
            {
              key: 'devboard-access',
              title: 'Доска задач разработки: ключи для чатов и доступ',
              hint: 'Ключ для claude.ai/code, люди доски',
              to: '/devboard?tab=access',
            },
          ]
        : [],
    [board],
  );
}
